import React from "react";
import { Link } from "react-router-dom";
import {
  Phone, Zap, Wrench, ShieldCheck, Clock, MapPin, Star,
  ArrowRight, CircleCheck, AlertTriangle, Plug, Lightbulb, Award,
  Search, Cable, Hammer,
} from "lucide-react";
import { SITE } from "../lib/site";
import { useLocation as useGeo } from "../contexts/LocationContext";
import SectionHeading from "../components/SectionHeading";
import Realisations from "../components/Realisations";
import UrgencyBar from "../components/UrgencyBar";
import BrandsStrip from "../components/BrandsStrip";
import LocalCoverage from "../components/LocalCoverage";
import useDocumentTitle from "../hooks/useDocumentTitle";

const HERO_IMG = "https://images.unsplash.com/photo-1621905251189-08b45d6a269e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzF8MHwxfHNlYXJjaHwzfHxwcm9mZXNzaW9uYWwlMjBlbGVjdHJpY2lhbiUyMHdvcmtpbmd8ZW58MHx8fHwxNzc4MTAyMDczfDA&ixlib=rb-4.1.0&q=85";
const TROUBLE_IMG = "https://images.pexels.com/photos/34054464/pexels-photo-34054464.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const INSTALL_IMG = "https://images.unsplash.com/photo-1774437290569-4c7061cae2c9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTZ8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBzbWFydCUyMGhvdXNlJTIwbGlnaHRpbmclMjBhcmNoaXRlY3R1cmV8ZW58MHx8fHwxNzc4MTAyMDc4fDA&ixlib=rb-4.1.0&q=85";

const TRUSTS = [
  { icon: Clock, title: "Intervention rapide", value: "30 min", desc: "Délai moyen en zone urbaine" },
  { icon: ShieldCheck, title: "Travail garanti", value: "2 ans", desc: "Sur l'ensemble des prestations" },
  { icon: Award, title: "Expérience", value: `${SITE.yearsExp}+ ans`, desc: "Au service des particuliers et pros" },
  { icon: CircleCheck, title: "Interventions", value: `${SITE.interventions}+`, desc: "Réalisées avec succès" },
];

const TESTIMONIALS = [
  { name: "Marie L.", city: "Boulogne-Billancourt", text: "Disjoncteur qui sautait sans arrêt — intervention en 25 minutes un dimanche soir. Travail propre, prix juste." , stars: 5},
  { name: "Karim B.", city: "Paris 11e", text: "Rénovation électrique complète d'un T3. Mise aux normes parfaite, conseils clairs, devis respecté à l'euro près.", stars: 5 },
  { name: "Sophie D.", city: "Levallois-Perret", text: "Pose d'un nouveau tableau et de plusieurs prises. Très professionnel, je recommande sans hésiter.", stars: 5 },
];

export default function Home() {
  const { displayCity, city } = useGeo();
  useDocumentTitle(
    city
      ? `Électricien à ${city} — Dépannage & Installation | Voltis Électricité`
      : "Voltis Électricité — Dépannage & Installation électrique",
    city
      ? `Électricien à ${city} : dépannage électrique d'urgence et installation aux normes. Intervention rapide 24/7, devis gratuit.`
      : "Électricien certifié : dépannage électrique d'urgence et installation aux normes. Intervention rapide 24/7, devis gratuit."
  );
  return (
    <div data-testid="home-page">
      {/* HERO — Asymmetrical split */}
      <section className="relative bg-white">
        <div className="container-x grid lg:grid-cols-12 gap-12 lg:gap-16 pt-16 pb-20 lg:pt-24 lg:pb-32">
          {/* Left content */}
          <div className="lg:col-span-7 flex flex-col justify-center animate-fade-up">
            <div className="inline-flex items-center self-start gap-2 bg-red-500 text-white px-3 py-1.5 text-xs font-semibold uppercase tracking-widest mb-8">
              <span className="emergency-dot" /> Dépannage d'urgence {SITE.emergency}
            </div>
            <h1 className="font-heading font-bold text-4xl sm:text-5xl lg:text-7xl leading-[1.05] tracking-tight text-slate-900">
              {city ? (
                <>
                  Électricien dépannage à{" "}
                  <span className="relative inline-block">
                    <span className="relative z-10" data-testid="hero-city">{city}</span>
                    <span className="absolute left-0 bottom-2 h-3 lg:h-4 w-full bg-[#EAB308] z-0" aria-hidden="true" />
                  </span>
                  .
                </>
              ) : (
                <>
                  Électricien{" "}
                  <span className="relative inline-block">
                    <span className="relative z-10" data-testid="hero-city">près de chez vous</span>
                    <span className="absolute left-0 bottom-2 h-3 lg:h-4 w-full bg-[#EAB308] z-0" aria-hidden="true" />
                  </span>
                  .
                </>
              )}
            </h1>
            <p className="mt-8 text-lg text-slate-600 max-w-xl leading-relaxed" data-testid="hero-seo-text">
              {city ? (
                <>
                  Vous recherchez un électricien à <strong className="text-slate-900">{city}</strong> pour
                  un dépannage rapide ou une installation électrique ? Nous intervenons pour les pannes électriques,
                  tableaux électriques, prises, éclairages, mises aux normes et urgences électriques. Devis possible,
                  intervention rapide dans votre ville.
                </>
              ) : (
                <>
                  Vous recherchez un électricien <strong className="text-slate-900">près de chez vous</strong> pour
                  un dépannage rapide ou une installation électrique ? Nous intervenons pour les pannes électriques,
                  tableaux électriques, prises, éclairages, mises aux normes et urgences électriques.
                </>
              )}
            </p>

            {/* CTAs */}
            <div className="mt-10 flex flex-col sm:flex-row gap-4">
              <a
                href={`tel:${SITE.phoneTel}`}
                data-testid="hero-call-btn"
                className="btn-primary text-base"
              >
                <Phone className="w-5 h-5" strokeWidth={2.5} />
                Appeler maintenant — {SITE.phoneDisplay}
              </a>
              <Link
                to="/contact"
                data-testid="hero-quote-btn"
                className="btn-outline text-base"
              >
                Demander un devis
                <ArrowRight className="w-5 h-5" />
              </Link>
            </div>

            {/* Mini trust line */}
            <div className="mt-10 flex flex-wrap items-center gap-x-6 gap-y-3 text-sm text-slate-500">
              <span className="inline-flex items-center gap-2"><CircleCheck className="w-4 h-4 text-[#EAB308]" /> Devis gratuit</span>
              <span className="inline-flex items-center gap-2"><CircleCheck className="w-4 h-4 text-[#EAB308]" /> Travail garanti 2 ans</span>
              <span className="inline-flex items-center gap-2"><CircleCheck className="w-4 h-4 text-[#EAB308]" /> {SITE.yearsExp}+ ans d'expérience</span>
            </div>
          </div>

          {/* Right image with sharp frame */}
          <div className="lg:col-span-5 relative">
            <div className="relative aspect-[4/5] overflow-hidden">
              <img
                src={HERO_IMG}
                alt="Électricien professionnel au travail"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-900/30 to-transparent" />
            </div>
            {/* Floating stat card */}
            <div className="absolute -bottom-8 -left-4 lg:-left-12 bg-[#0F172A] text-white px-6 py-5 max-w-[260px] shadow-xl">
              <div className="flex items-center gap-1 mb-2">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Star key={i} className="w-4 h-4 fill-[#EAB308] text-[#EAB308]" />
                ))}
              </div>
              <p className="text-sm leading-relaxed">
                <strong className="text-[#EAB308]">{SITE.interventions}+</strong> interventions réalisées avec succès dans votre secteur.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* URGENCY BAR — red persistent CTA */}
      <UrgencyBar />

      {/* LOCAL COVERAGE — section dédiée à la ville détectée */}
      <LocalCoverage />

      {/* TRUST INDICATORS */}
      <section className="bg-[#0F172A] text-white">
        <div className="container-x py-14 grid grid-cols-2 lg:grid-cols-4 gap-8">
          {TRUSTS.map((t) => (
            <div key={t.title} data-testid={`trust-${t.title.toLowerCase().replace(/\s/g, '-')}`} className="flex flex-col">
              <t.icon className="w-7 h-7 text-[#EAB308] mb-4" strokeWidth={1.5} />
              <div className="font-heading text-3xl lg:text-4xl font-bold">{t.value}</div>
              <div className="text-sm font-medium mt-1">{t.title}</div>
              <div className="text-xs text-slate-400 mt-1">{t.desc}</div>
            </div>
          ))}
        </div>
      </section>

      {/* SERVICES PRINCIPAUX — 7 services en grille */}
      <section className="section-padding bg-white" id="services">
        <div className="container-x">
          <SectionHeading
            eyebrow="Nos services principaux"
            title="Sept expertises pour tous vos besoins."
            description="Dépannage en urgence, installation, mise aux normes, tableau, recherche de panne, rénovation — un seul interlocuteur pour tout."
          />
          <div className="mt-14 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {[
              { icon: AlertTriangle, label: "Dépannage électrique", to: "/depannage", color: "red" },
              { icon: Cable, label: "Installation électrique", to: "/installation" },
              { icon: ShieldCheck, label: "Mise aux normes", to: "/installation" },
              { icon: Plug, label: "Tableau électrique", to: "/installation" },
              { icon: Search, label: "Recherche de panne", to: "/depannage" },
              { icon: Zap, label: "Urgence électrique", to: "/depannage", color: "red" },
              { icon: Hammer, label: "Rénovation électrique", to: "/installation" },
              { icon: Lightbulb, label: "Éclairage int. & ext.", to: "/installation" },
            ].map((s) => (
              <Link
                key={s.label}
                to={s.to}
                data-testid={`service-${s.label.toLowerCase().replace(/[^a-z]/g, '-').slice(0, 20)}`}
                className="group flex flex-col items-start p-6 border border-slate-200 hover:border-slate-900 hover:bg-slate-50 transition-all"
              >
                <span className={`inline-flex w-11 h-11 items-center justify-center mb-4 ${s.color === 'red' ? 'bg-red-500 text-white' : 'bg-[#EAB308] text-slate-900'} group-hover:scale-110 transition-transform`}>
                  <s.icon className="w-5 h-5" strokeWidth={2} />
                </span>
                <span className="font-heading font-semibold text-slate-900 text-base leading-snug">{s.label}</span>
                <ArrowRight className="w-4 h-4 text-slate-400 mt-3 group-hover:translate-x-1 group-hover:text-[#CA8A04] transition-all" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICES PREVIEW — 2 grandes cartes */}
      <section className="section-padding bg-slate-50">
        <div className="container-x">
          <SectionHeading
            eyebrow="Nos prestations"
            title="Deux expertises, une même exigence."
            description="Du dépannage en urgence à l'installation complète aux normes — votre électricien de confiance pour tous vos besoins."
          />

          <div className="grid lg:grid-cols-2 gap-8 lg:gap-10 mt-14">
            {/* Card 1 — Dépannage */}
            <Link
              to="/depannage"
              data-testid="service-card-depannage"
              className="group block relative overflow-hidden border border-slate-200 hover:border-slate-900 transition-all bg-white"
            >
              <div className="aspect-[16/10] overflow-hidden">
                <img src={TROUBLE_IMG} alt="Dépannage électrique" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
              </div>
              <div className="p-8">
                <div className="flex items-center gap-3 mb-4">
                  <span className="inline-flex w-10 h-10 items-center justify-center bg-red-500 text-white">
                    <AlertTriangle className="w-5 h-5" />
                  </span>
                  <span className="text-xs font-bold uppercase tracking-widest text-red-500">Urgence {SITE.emergency}</span>
                </div>
                <h3 className="font-heading text-2xl lg:text-3xl font-bold text-slate-900 mb-3">
                  Dépannage électrique
                </h3>
                <p className="text-slate-600 leading-relaxed mb-6">
                  Panne, court-circuit, disjoncteur qui saute, recherche de panne, remise en service.
                  Intervention rapide jour et nuit.
                </p>
                <div className="inline-flex items-center gap-2 text-sm font-semibold text-slate-900 group-hover:text-[#CA8A04] transition-colors">
                  Voir les interventions <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </div>
              </div>
            </Link>

            {/* Card 2 — Installation */}
            <Link
              to="/installation"
              data-testid="service-card-installation"
              className="group block relative overflow-hidden border border-slate-200 hover:border-slate-900 transition-all bg-white"
            >
              <div className="aspect-[16/10] overflow-hidden">
                <img src={INSTALL_IMG} alt="Installation électrique" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
              </div>
              <div className="p-8">
                <div className="flex items-center gap-3 mb-4">
                  <span className="inline-flex w-10 h-10 items-center justify-center bg-[#EAB308] text-slate-900">
                    <Wrench className="w-5 h-5" strokeWidth={2.5} />
                  </span>
                  <span className="text-xs font-bold uppercase tracking-widest text-slate-900">Sur devis</span>
                </div>
                <h3 className="font-heading text-2xl lg:text-3xl font-bold text-slate-900 mb-3">
                  Installation électrique
                </h3>
                <p className="text-slate-600 leading-relaxed mb-6">
                  Installation complète, rénovation, mise aux normes, tableau électrique,
                  prises, interrupteurs et éclairage intérieur/extérieur.
                </p>
                <div className="inline-flex items-center gap-2 text-sm font-semibold text-slate-900 group-hover:text-[#CA8A04] transition-colors">
                  Voir les prestations <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </div>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* WHY US */}
      <section className="bg-white section-padding">
        <div className="container-x grid lg:grid-cols-12 gap-14">
          <div className="lg:col-span-5">
            <SectionHeading
              eyebrow="Pourquoi nous choisir"
              title="Sérieux, rapidité, transparence."
              description={`Plus de ${SITE.yearsExp} années à dépanner et installer pour des particuliers comme des professionnels. Une équipe certifiée, des tarifs clairs et un travail soigné.`}
            />
            <Link to="/a-propos" className="btn-secondary mt-8">
              En savoir plus <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="lg:col-span-7 grid sm:grid-cols-2 gap-5">
            {[
              { icon: Zap, title: "Devis gratuit", desc: "Estimation claire et transparente, sans surprise." },
              { icon: Clock, title: "Disponible 7j/7", desc: "Urgences traitées même la nuit et les week-ends." },
              { icon: ShieldCheck, title: "Garantie 2 ans", desc: "Sur toutes nos interventions et installations." },
              { icon: CircleCheck, title: "Aux normes NF C 15-100", desc: "Travaux conformes à la réglementation en vigueur." },
            ].map((f) => (
              <div key={f.title} className="bg-white border border-slate-200 p-6 hover:border-slate-900 transition-colors">
                <f.icon className="w-7 h-7 text-[#EAB308] mb-4" strokeWidth={1.75} />
                <h4 className="font-heading text-lg font-semibold text-slate-900 mb-2">{f.title}</h4>
                <p className="text-sm text-slate-600 leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="section-padding bg-slate-50">
        <div className="container-x">
          <SectionHeading
            eyebrow="Avis clients"
            title="Ce qu'ils disent de nous."
            align="center"
          />
          <div className="mt-14 grid md:grid-cols-3 gap-6">
            {TESTIMONIALS.map((t, i) => (
              <div
                key={i}
                data-testid={`testimonial-${i}`}
                className="bg-white border border-slate-200 p-7 flex flex-col"
              >
                <div className="flex items-center gap-1 mb-4">
                  {Array.from({ length: t.stars }).map((_, j) => (
                    <Star key={j} className="w-4 h-4 fill-[#EAB308] text-[#EAB308]" />
                  ))}
                </div>
                <p className="text-slate-700 leading-relaxed flex-1">"{t.text}"</p>
                <div className="mt-6 pt-5 border-t border-slate-200">
                  <p className="font-semibold text-slate-900">{t.name}</p>
                  <p className="text-xs text-slate-500 flex items-center gap-1.5 mt-1">
                    <MapPin className="w-3 h-3" /> {t.city}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* REALISATIONS */}
      <Realisations />

      {/* BRANDS STRIP — bandeau discret au milieu */}
      <BrandsStrip />

      {/* CITIES SHORT — sans liste de villes */}
      <section className="section-padding bg-slate-50">
        <div className="container-x">
          <div className="grid lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-8">
              <SectionHeading
                eyebrow="Zone d'intervention"
                title={city ? `Intervention rapide à ${city} et alentours.` : "Intervention rapide près de chez vous."}
                description="Nous nous déplaçons rapidement chez vous et dans toutes les communes voisines."
              />
            </div>
            <div className="lg:col-span-4 lg:text-right">
              <Link to="/zone" className="btn-outline">
                Voir la carte <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="bg-[#0F172A] text-white">
        <div className="container-x py-20 lg:py-28 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-8">
            <span className="accent-bar mb-5" />
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight">
              Une urgence ou un projet ?<br />
              <span className="text-[#EAB308]">Parlons-en maintenant.</span>
            </h2>
            <p className="mt-5 text-slate-300 text-lg max-w-2xl">
              Devis gratuit, conseils clairs et intervention rapide{city ? ` à ${city}` : " près de chez vous"}.
            </p>
          </div>
          <div className="lg:col-span-4 flex flex-col gap-3">
            <a href={`tel:${SITE.phoneTel}`} data-testid="cta-call-btn" className="btn-primary justify-center text-base">
              <Phone className="w-5 h-5" /> {SITE.phoneDisplay}
            </a>
            <Link to="/contact" data-testid="cta-contact-btn" className="bg-white text-slate-900 hover:bg-slate-100 inline-flex items-center justify-center gap-2 px-6 py-3.5 font-semibold rounded-sm transition-colors">
              Demander un devis <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
